import React, { useEffect, useState } from "react";
import { common } from "../js/common";
import CardComponent from "./subComponents/searchCard";
import { Col, Container, Row } from "react-bootstrap";

interface Recipe {
  id: number;
  name: string;
  description: string;
  creatorId: number;
  createdOn: string;
  previewImageUrl: string | null;
}

interface RecipesResponse {
  recipes?: Recipe[];
  error?: string;
}

function HomePage() {
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [errorMessage, setError] = useState<string>("");

  useEffect(() => {
    async function fetchRecipes(): Promise<void> {
      try {
        const response = await common.fetchRestAPI("recipes", "GET");

        if (!response.ok) {
          let msg = `HTTP error! status: ${response.status}`;
          try {
            const errorData = await response.json();
            if (errorData?.error) {
              msg = errorData.error;
            }
          } catch {
            /* response wasn't JSON */
          }
          setError(msg);
          throw new Error(msg);
        }

        const data: RecipesResponse = await response.json();

        if (!data.recipes) {
          const msg = "No recipes list returned from backend";
          setError(msg);
          throw new Error(msg);
        }
        setLoading(false);
        setRecipes(data.recipes || []);
      } catch (error) {
        setLoading(false);
        let userErrorMessage = "An error occurred obtaining recipes";
        console.error(`${userErrorMessage}: `, error);
        if (error instanceof Error) {
          if (
            error.message === "Failed to fetch" ||
            error.message.includes("HTTP error")
          ) {
            userErrorMessage = "Server Down";
          }
        }
        setError(userErrorMessage);
      }
    }
    fetchRecipes();
  }, []);

  return (
    <Container>
      <Container className="p-5" style={{ maxWidth: "768px" }}>
        <h1>Family Recipes</h1>
        <Row className="m-2">
          <Col>
            <p>
              Welcome to the family recipes website! Preserve your recipes
              digitally here.
            </p>
            <p>
              Don't have time to type them up? Upload scans to convert to text
              later!
            </p>
          </Col>
          <Col className="col-3 text-end">
            <a href="/submit" className="btn btn-success">
              Submit
            </a>
          </Col>
        </Row>
      </Container>
      <div>
        {loading ? (
          <div className="d-flex justify-content-center align-items-center">
            <div
              className="spinner-border text-primary spinner-border-sm"
              role="status"
            >
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : errorMessage !== "" ? (
          <p className="alert alert-warning text-center">{errorMessage}</p>
        ) : recipes.length === 0 ? (
          <p>
            No recipes yet. <a href="/submit">Get upload'n</a>!
          </p>
        ) : (
          recipes.reduce<React.JSX.Element[]>((rows, recipe, index) => {
            if (index % 2 === 0) {
              rows.push(
                <Row key={`row-${index}`}>
                  <CardComponent
                    id={recipe.id}
                    name={recipe.name}
                    description={recipe.description}
                    previewImageUrl={recipe.previewImageUrl}
                    createdOn={recipe.createdOn}
                  />
                  {recipes[index + 1] && (
                    <CardComponent
                      id={recipes[index + 1].id}
                      name={recipes[index + 1].name}
                      description={recipes[index + 1].description}
                      previewImageUrl={recipes[index + 1].previewImageUrl}
                      createdOn={recipes[index + 1].createdOn}
                    />
                  )}
                </Row>,
              );
            }
            return rows;
          }, [])
        )}
      </div>
    </Container>
  );
}

export default HomePage;
